<script lang="ts">
import { defineComponent } from 'vue';
import algoliasearch from 'algoliasearch/lite';
import clickOutside from '../directives/clickOutside';

import {
  AisHits,
  AisInstantSearch,
  AisSearchBox,
  AisStateResults,
  AisStats,
} from 'vue-instantsearch/vue3/es';

export default defineComponent({
  components: {
    AisHits,
    AisInstantSearch,
    AisSearchBox,
    AisStateResults,
    AisStats,
  },
  directives: {
    clickOutside,
  },
  props: {
    searchApiKey: {
      type: String,
      required: true,
    },
    appId: {
      type: String,
      required: true,
    },
    environment: {
      type: String,
      required: true,
    },
    isDesktop: {
      type: Boolean,
      required: true,
    },
    isMobileMenuEnabled: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isDesktopEnabled: false,
      searchClient: algoliasearch(this.appId, this.searchApiKey),
    };
  },
  computed: {
    indexName() {
      return `${this.environment}_headerSearch`;
    },
    isEnabled() {
      return this.isDesktop ? this.isDesktopEnabled : this.isMobileMenuEnabled;
    },
  },
  watch: {
    /**
     * Open / close search if device width/orientation changes
     */
    isDesktop: {
      immediate: true,
      handler(isDesktop: boolean) {
        this.isDesktopEnabled = !isDesktop;
      },
    },
  },
  methods: {
    clickOutside() {
      if (this.isDesktop) {
        this.isDesktopEnabled = false;
      }
    },
    focusSearchInput() {
      const { searchBox } = this.$refs as {
        searchBox: { $refs: { searchInput: { $refs: { input: HTMLInputElement } } } };
      };
      if (!searchBox?.$refs?.searchInput?.$refs?.input) {
        // If refs aren't available yet, try again after a short delay
        setTimeout(() => this.focusSearchInput(), 100);
        return;
      }

      const input = searchBox.$refs.searchInput.$refs.input;
      if (input instanceof HTMLInputElement) {
        input.focus();
      }
    },
  },
});
</script>
<template>
  <div
    v-click-outside.stop="clickOutside"
    class="header-search relative px-8 lg:flex lg:items-center lg:p-0"
  >
    <button
      type="button"
      class="hidden lg:block"
      aria-label="Open search bar"
      title="Open search bar"
      aria-controls="header-search"
      :aria-expanded="isDesktopEnabled"
      @click="isDesktopEnabled = true"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        class="cursor-pointer hover:text-menzies-yellow-300"
        @click.prevent="focusSearchInput"
      >
        <path
          d="M17 17L12.3333 12.3333M13.8889 8.44444C13.8889 11.4513 11.4513 13.8889 8.44444 13.8889C5.43756 13.8889 3 11.4513 3 8.44444C3 5.43756 5.43756 3 8.44444 3C11.4513 3 13.8889 5.43756 13.8889 8.44444Z"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
    <Transition @after-enter="focusSearchInput">
      <div v-show="isEnabled" id="header-search" class="py-2.5 lg:py-0">
        <div
          class="z-50 mr-[1.25rem] rounded-2xl border-(length:--search-border-width) border-[#d9d9d9] bg-white text-menzies-neutral-900 shadow-lg focus-within:ring-2 focus-within:ring-menzies-yellow-300 lg:absolute lg:top-0 lg:-right-(--search-right) lg:rounded-lg [&.v-enter-active]:transition-opacity [&.v-enter-from]:opacity-0 [&.v-leave-active]:transition-opacity [&.v-leave-to]:opacity-0"
        >
          <ais-instant-search :search-client="searchClient" :index-name="indexName">
            <ais-search-box
              ref="searchBox"
              show-loading-indicator
              placeholder="Search"
              :class-names="{
                'ais-SearchBox-form': 'relative',
                'ais-SearchBox-input':
                  'placeholder:text-menzies-neutral-500 w-full border-0 bg-transparent px-4 leading-[2.375rem] focus:ring-0 focus:outline-none lg:w-auto lg:pr-(--search-input-padding-right) lg:pl-(--search-inline-padding) [&::-webkit-search-cancel-button]:hidden',
                'ais-SearchBox-reset':
                  '-translate-y-1/2 absolute right-(--search-reset-offset) top-1/2',
                'ais-SearchBox-submit':
                  '-translate-y-1/2 absolute right-(--search-inline-padding) top-1/2',
                'ais-SearchBox-loadingIndicator':
                  '-translate-y-1/2 absolute right-(--search-inline-padding) top-1/2',
              }"
            >
              <template #submit-icon>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  class="text-menzies-neutral-900"
                  @click.prevent="focusSearchInput"
                >
                  <path
                    d="M17 17L12.3333 12.3333M13.8889 8.44444C13.8889 11.4513 11.4513 13.8889 8.44444 13.8889C5.43756 13.8889 3 11.4513 3 8.44444C3 5.43756 5.43756 3 8.44444 3C11.4513 3 13.8889 5.43756 13.8889 8.44444Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </template>
            </ais-search-box>
            <ais-stats>
              <template #default="{ nbHits, query }">
                <section v-if="nbHits > 0 && query" class="px-4 pt-3.5 pb-5">
                  <div
                    class="[scrollbar-color:theme(colors.gtg.pink.500)_theme(colors.gtg.grey.200)] max-h-[21rem] overflow-scroll pr-3 [scrollbar-width:thin]"
                  >
                    <ais-state-results>
                      <ais-hits>
                        <template #default="{ items /* , sendEvent, query */ }">
                          <ul class="flex flex-col justify-start divide-y border-t">
                            <li v-for="item in items" :key="item.objectID">
                              <a :href="item.url" class="block py-3 text-base hover:underline">
                                <div class="font-medium">{{ item.title }}</div>
                                <!-- Career details -->
                                <div
                                  v-if="item.sectionHandle === 'careers'"
                                  class="mt-1 text-sm font-semibold text-gray-600"
                                >
                                  <div v-if="item.location">
                                    <span class="font-bold">Location:</span> {{ item.location }}
                                  </div>
                                  <div v-if="item.salary">
                                    <span class="font-bold">Salary:</span> {{ item.salary }}
                                  </div>
                                  <div v-if="item.jobType">
                                    <span class="font-bold">Contract:</span> {{ item.jobType }}
                                  </div>
                                  <div v-if="item.jobHours">
                                    <span class="font-bold">Hours:</span> {{ item.jobHours }}
                                  </div>
                                  <div v-if="item.shift">
                                    <span class="font-bold">Shift:</span> {{ item.shift }}
                                  </div>
                                  <div v-if="item.industry">
                                    <span class="font-bold">Industry:</span> {{ item.industry }}
                                  </div>
                                </div>
                              </a>
                            </li>
                          </ul>
                        </template>
                      </ais-hits>
                    </ais-state-results>
                  </div>
                </section>
                <section v-else-if="nbHits === 0 && query.length > 0" class="px-4 pt-3.5 pb-5">
                  <p class="border-t py-2 text-sm">No results have been found for {{ query }}.</p>
                </section>
                <!-- Blank for the else as we want no output -->
                <div v-else></div>
              </template>
            </ais-stats>
          </ais-instant-search>
        </div>
      </div>
    </Transition>
  </div>
</template>
